import * as React from 'react';
import { UseFormRegister, UseFormWatch, UseFormSetValue } from 'react-hook-form';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { Checkbox } from '@/components/ui/Checkbox';
import type { StoreProfileFormData } from '@/lib/validators';

interface StoreHoursFormProps {
  register: UseFormRegister<StoreProfileFormData>;
  watch: UseFormWatch<StoreProfileFormData>;
  setValue: UseFormSetValue<StoreProfileFormData>;
}

const DAYS = [
  { value: 'mon', label: 'Monday' },
  { value: 'tue', label: 'Tuesday' },
  { value: 'wed', label: 'Wednesday' },
  { value: 'thu', label: 'Thursday' },
  { value: 'fri', label: 'Friday' },
  { value: 'sat', label: 'Saturday' },
  { value: 'sun', label: 'Sunday' },
] as const;

export function StoreHoursForm({ register, watch, setValue }: StoreHoursFormProps) {
  const operatingHours = watch('operatingHours') || [];

  // Initialize with default hours if empty
  React.useEffect(() => {
    if (operatingHours.length === 0) {
      setValue(
        'operatingHours',
        DAYS.map((day) => ({
          day: day.value,
          open: '10:00',
          close: '21:00',
          closed: false,
        }))
      );
    }
  }, [operatingHours.length, setValue]);

  const toggleClosed = (index: number) => {
    const current = operatingHours[index];
    if (current) {
      setValue(`operatingHours.${index}.closed`, !current.closed);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Operating Hours</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          {DAYS.map((day, index) => {
            const hours = operatingHours[index];
            const isClosed = hours?.closed || false;

            return (
              <div key={day.value} className="flex items-center gap-4">
                <div className="w-28 text-sm font-medium">{day.label}</div>
                <div className="flex flex-1 items-center gap-2">
                  <Input
                    type="time"
                    {...register(`operatingHours.${index}.open`)}
                    disabled={isClosed}
                    className="w-32"
                  />
                  <span className="text-sm text-muted-foreground">to</span>
                  <Input
                    type="time"
                    {...register(`operatingHours.${index}.close`)}
                    disabled={isClosed}
                    className="w-32"
                  />
                  <Checkbox
                    label="Closed"
                    checked={isClosed}
                    onChange={() => toggleClosed(index)}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}
